import React, {useEffect, useMemo, useState} from 'react';
import {useAppDispatch, useAppSelector} from "../../../app/store";
import {Form, Modal, Tabs, Tab, Col, Row, Button, Alert, Dropdown} from "react-bootstrap";
import {Stars} from "react-bootstrap-icons";
import {
    changeQuestionModal,
    closeQuestionModal,
    deleteQuestion,
    deleteSectionElement,
    deleteSlide,
    EditQuestionPayload,
    getElement,
    getQuestion,
    getSlide,
    NewQuestionPayload,
    saveElement,
    saveNewQuestion,
    saveNewSlide,
    saveQuestion,
    saveSlide,
    setActiveLanguage, translateElement, translateQuestion, translateSlide
} from "../../../app/slices/modals/questionEditModal";
import {ADMIN_LANG, FEATURE_AI_TRANSLATION} from "../../../settings";

import AdminLoader from "../AdminLoader";
import QuestionEditSelect from "./questionEditModal/QuestionEditSelect";
import QuestionEditOrder from "./questionEditModal/QuestionEditOrder";
import QuestionEditConnect from "./questionEditModal/QuestionEditConnect";
import QuestionEditSlide from "./questionEditModal/QuestionEditSlide";
import QuestionEditOpen from "./questionEditModal/QuestionEditOpen";

import {ExclamationTriangleFill} from "react-bootstrap-icons";
import Confirm from "../Confirm";
import ElementModal from "./questionEditModal/ElementModal";
import MediaEditImage from "./questionEditModal/MediaEditImage";
import {v4 as uuidv4} from "uuid";
import QuestionEditScale from "./questionEditModal/QuestionEditScale";


interface LanguageImages {
    [key: string]: File | null;
}

const QuestionEditModal = ({is_draft}: { is_draft: boolean }) => {
    const dispatch = useAppDispatch();
    const {current_version_id, game_id, active_languages} = useAppSelector((state) => state.adminGameView);
    const {
        isOpen, loading, content_id,
        type, languages,
        section_id, select_options, order_options,
        connect_options, points, multiple, shuffle, languageTab, error,
        only_title, text_options, layout, default_image, time_limit,
        slide_rows, max_scale, min_scale, number_scale_options, rating_type, allow_skip
    } = useAppSelector((state) => state.questionEditModal);

    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [languageImages, setLanguageImages] = useState<LanguageImages>({});
    const [defaultImage, setDefaultImage] = useState<File | null>(null);

    useEffect(() => {
        if (isOpen && content_id && game_id) {
            if (type === 'slide') {
                dispatch(getSlide({game_id, version_id: current_version_id, content_id}));
            } else if (type.startsWith('element_')) {
                dispatch(getElement({game_id, version_id: current_version_id, content_id}));
            } else {
                dispatch(getQuestion({game_id, version_id: current_version_id, content_id}));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, content_id, current_version_id, game_id]);


    const handleTranslate = (translate_from: string | null) => {
        if (content_id && game_id && current_version_id && section_id && FEATURE_AI_TRANSLATION && translate_from) {
            if (type === 'slide') {
                dispatch(translateSlide({
                    type,
                    game_id,
                    version_id: current_version_id,
                    section_id,
                    content_id,
                    languages,
                    translate_to: languageTab,
                    translate_from,
                    only_title,
                    slide_rows,
                }));
            } else if (type.startsWith('element_')) {
                const formData = new FormData();
                formData.append('type', type);
                formData.append('game_id', game_id);
                formData.append('version_id', current_version_id);
                formData.append('section_id', section_id);
                formData.append('content_id', content_id);
                formData.append('languages', JSON.stringify(languages));
                formData.append('translate_to', languageTab);
                formData.append('translate_from', translate_from);
                dispatch(translateElement(formData));
            } else {
                dispatch(translateQuestion({
                    type,
                    game_id,
                    version_id: current_version_id,
                    section_id,
                    content_id,
                    languages,
                    select_options,
                    order_options,
                    connect_options,
                    number_scale_options,
                    text_options,
                    translate_to: languageTab,
                    translate_from,
                    points,
                    multiple,
                    shuffle,
                    layout,
                    time_limit,
                }));
            }
        }
    }

    const handleSave = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (type === 'slide') {
            if (content_id && section_id) {
                dispatch(saveSlide({
                    type,
                    game_id,
                    version_id: current_version_id,
                    languages,
                    section_id,
                    content_id,
                    only_title,
                }));
            } else if (section_id) {
                dispatch(saveNewSlide({
                    type,
                    game_id,
                    version_id: current_version_id,
                    languages,
                    section_id,
                    only_title,
                }));
            }
        } else if (type.startsWith('element_')) {

            if (section_id) {
                const formData = new FormData();
                formData.append('type', type);
                formData.append('game_id', game_id);
                formData.append('version_id', current_version_id);
                formData.append('section_id', section_id);
                formData.append('language', languageTab);
                formData.append('languages', JSON.stringify(languages));
                if (content_id) {
                    formData.append('content_id', content_id);
                }

                Object.entries(languageImages).forEach(([lang, file]) => {
                    if (file) {
                        formData.append(`language_image_${lang}`, file, uuidv4() + file.name);
                    } else {
                        const existingImage = languages.find(l => l.language_code === lang)?.image;
                        if (existingImage) {
                            formData.append(`language_image_${lang}`, existingImage.name);
                        }
                    }
                });

                if (defaultImage) {
                    formData.append('default_image', defaultImage, uuidv4() + defaultImage.name);
                } else if (default_image) {
                    formData.append('default_image', default_image.name);
                }

                // formData.forEach((value, key) => {
                //     console.log(`${key}:`, value);
                // });
                dispatch(saveElement(formData));
            }
        } else {
            const questionPayload = {
                type,
                game_id,
                version_id: current_version_id,
                section_id,
                languages,
                select_options,
                order_options,
                connect_options,
                text_options,
                points,
                multiple,
                shuffle,
                layout,
                time_limit,
                min_scale,
                max_scale,
                number_scale_options,
                rating_type,
                allow_skip,
                ...(content_id && {content_id}),
            };
            if (content_id) {
                dispatch(saveQuestion(questionPayload as EditQuestionPayload));
            } else {
                dispatch(saveNewQuestion(questionPayload as NewQuestionPayload));
            }
        }
    };

    const handleDelete = () => {
        if (content_id) {
            setShowConfirmDelete(true);
        }
    };

    const handleConfirm = () => {
        setShowConfirmDelete(false);
        if (content_id && type === 'slide') {
            dispatch(deleteSlide({content_id, game_id, version_id: current_version_id}));
        } else if (content_id && type.startsWith('element_')) {
            dispatch(deleteSectionElement({content_id, game_id, version_id: current_version_id}));
        } else if (content_id) {
            dispatch(deleteQuestion({content_id, game_id, version_id: current_version_id}));
        }
    };

    const handleClose = () => {
        dispatch(closeQuestionModal());
    };

    const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        dispatch(changeQuestionModal({type: e.target.value}));
    };

    const handleTabSelect = (key: string | null) => {
        if (key) {
            dispatch(setActiveLanguage(key));
        }
    };

    const handlePointsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(changeQuestionModal({points: parseInt(e.target.value)}));
    }

    const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const updatedLanguages = languages.map((language) => {
            if (language.language_code === languageTab) {
                return {...language, description: e.target.value};
            }
            return language;
        });
        dispatch(changeQuestionModal({languages: updatedLanguages}));
    }

    const handleOutroTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const updatedLanguages = languages.map((language) => {
            if (language.language_code === languageTab) {
                return {...language, outro_text: e.target.value};
            }
            return language;
        });
        dispatch(changeQuestionModal({languages: updatedLanguages}));
    }

    const handleTimeLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(changeQuestionModal({time_limit: parseInt(e.target.value)}));
    }

    const handleTitleChange = (title: string) => {
        let languageExists = false;

        const updatedLanguages = languages.map((language) => {
            if (language.language_code === languageTab) {
                languageExists = true;
                return {...language, title};
            }
            return language;
        });

        if (!languageExists) {
            updatedLanguages.push({language_code: languageTab, title});
        }
        dispatch(changeQuestionModal({languages: updatedLanguages}));
    }

    const handleLanguageImageChange = (image: File | null, language: string) => {
        if (image) {
            setLanguageImages(prev => ({...prev, [language]: image}));
        } else {
            setLanguageImages(prev => {
                const newImages = {...prev};
                delete newImages[language];
                return newImages;
            });
            const updatedLanguages = languages.map((lang) => {
                if (lang.language_code === language) {
                    return {...lang, image: null};
                }
                return lang;
            });
            dispatch(changeQuestionModal({languages: updatedLanguages}));
        }
    }
    const handleDefaultImageChange = (image: File | null) => {
        if (image) {
            setDefaultImage(image);
        } else {
            setDefaultImage(null);
            dispatch(changeQuestionModal({default_image: null}));
        }
    }

    let content: React.ReactNode;

    switch (type) {
        case 'slide':
            if (content_id) {
                content = <QuestionEditSlide/>;
            }
            break;
        case 'select':
            content = <QuestionEditSelect language={languageTab} is_draft={is_draft}/>;
            break;
        case 'order':
            content = <QuestionEditOrder language={languageTab}
                                         handlePointsChange={handlePointsChange} is_draft={is_draft}/>;
            break;
        case 'connect':
            content = <QuestionEditConnect language={languageTab}
                                           handlePointsChange={handlePointsChange} is_draft={is_draft}/>;
            break;
        case 'number_scale':
            content = <QuestionEditScale language={languageTab} is_draft={is_draft}/>;
            break;
        case 'text':
            content = <QuestionEditOpen language={languageTab} is_draft={is_draft}/>;
            break;
        case 'element_image':
            content = <MediaEditImage
                active_languages={active_languages}
                default_image={default_image}
                language={languageTab}
                is_draft={is_draft}
                languages={languages}
                handleLanguageImageChange={handleLanguageImageChange}
                handleDefaultImageChange={handleDefaultImageChange}
            />;
            break
        default:
            content = null;
    }

    const handleLayOutChange = (layout: string) => {
        dispatch(changeQuestionModal({layout}));
    }

    const questionTitle = useMemo(() => {
        if (content_id) {
            const question = languages.find((lang) => lang.language_code === languageTab);
            return question?.title || '';
        }
        return ADMIN_LANG.new_content;
    }, [content_id, languages, languageTab]);


    const languageExists = languages.find(lang => lang.language_code === languageTab);
    const titleHelpText = languages
        .filter(lang => lang.language_code !== languageTab && lang.title)
        .map(lang => (
            <div key={lang.language_code}>
                <strong>{lang.language_code.toUpperCase()}: </strong>
                {lang.title}
            </div>
        ));

    let continueText = ADMIN_LANG.save;
    if (type && type === 'slide' && !content_id) {
        continueText = ADMIN_LANG.continue;
    }
    if (type && type === 'number_scale' && !content_id) {
        continueText = ADMIN_LANG.continue;
    }

    const languageWarning = languages.find(
        lang => lang.language_code === languageTab && lang.warnings
    ) || {warnings: ''};

    const {showOutro, showDescription, titleText} = {
        showOutro: type !== 'slide' && !type.startsWith('element_'),
        showDescription: type !== 'slide' && !type.startsWith('element_'),
        titleText: `${type !== 'slide' && !type.startsWith('element_') ? ADMIN_LANG.question : ADMIN_LANG.title} (${languageTab.toUpperCase()})`
    };

    return (
        <Modal size='xl' show={isOpen} onHide={handleClose} className='admin-modal'>
            {loading && <AdminLoader/>}
            <Modal.Header closeButton>
                <Modal.Title>{questionTitle}</Modal.Title>
            </Modal.Header>
            <Modal.Body className='container-fluid'>
                {error && (
                    <Alert variant='danger' dismissible>
                        <div dangerouslySetInnerHTML={{__html: error}}/>
                    </Alert>
                )}
                {languageWarning.warnings && (
                    <Alert variant='warning'>
                        <div dangerouslySetInnerHTML={{__html: languageWarning.warnings}}/>
                    </Alert>
                )}
                {!is_draft &&
                    <p className='text-info fw-semibold'>
                        <ExclamationTriangleFill className="me-2"/>
                        {ADMIN_LANG.draft_info}
                    </p>
                }
                <Form id='question-modal-form' className='row gy-3' onSubmit={handleSave}>
                    {!content_id &&
                        <Col>
                            <Form.Group controlId={`type_${languageTab}`}>
                                <Form.Label>{ADMIN_LANG.content_type}</Form.Label>
                                <Form.Select onChange={handleTypeChange} value={type} id='type'>
                                    <option disabled value=''>{ADMIN_LANG.select_content_type}</option>
                                    <optgroup label="Kysymykset">
                                        <option value='select'>{ADMIN_LANG.type_select}</option>
                                        <option value='order'>{ADMIN_LANG.type_order}</option>
                                        <option value='connect'>{ADMIN_LANG.type_connect}</option>
                                        <option value='text'>{ADMIN_LANG.type_text}</option>
                                        <option value='number_scale'>{ADMIN_LANG.number_scale}</option>
                                    </optgroup>
                                    <optgroup label="Muut">
                                        <option value='slide'>{ADMIN_LANG.type_slide}</option>
                                        <option value='element_title'>{ADMIN_LANG.type_title}</option>
                                        <option value='element_image'>{ADMIN_LANG.type_image}</option>
                                    </optgroup>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    }

                    <Col xs={12}>
                        <Tabs activeKey={languageTab} onSelect={handleTabSelect}>
                            {active_languages.map(language => (
                                <Tab eventKey={language} title={language.toUpperCase()} key={language}>
                                    {content_id && active_languages.length > 1 && FEATURE_AI_TRANSLATION &&
                                        <div className="d-flex justify-content-end mb-1 mt-3">
                                            <Dropdown onSelect={(eventKey) => handleTranslate(eventKey)}>
                                                <Dropdown.Toggle variant="outline-secondary" id="translate-dropdown">
                                                    {ADMIN_LANG.translate_from} <Stars className="ms-1"/>
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu>
                                                    {active_languages.filter(lang => lang !== language).map(lang => (
                                                        <Dropdown.Item eventKey={lang}
                                                                       key={lang}>{lang.toUpperCase()}</Dropdown.Item>
                                                    ))}
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </div>
                                    }
                                    <Row className='gy-3 pt-3'>
                                        <Col xs={12}>
                                            <Form.Group controlId={`title_${language}`}>
                                                <Form.Label>{titleText}</Form.Label>
                                                <Form.Control
                                                    maxLength={255}
                                                    type='text'
                                                    value={languages.find((lang) => lang.language_code === language)?.title || ''}
                                                    onChange={(e) => handleTitleChange(e.target.value)}
                                                    required={language === languageTab}
                                                />
                                                {(titleHelpText.length > 0 || type === 'element_image') &&
                                                    <Form.Text className='help-text'>
                                                        {type === 'element_image' &&
                                                            <div>{ADMIN_LANG.element_image_title_help}</div>
                                                        }
                                                        {titleHelpText}
                                                    </Form.Text>
                                                }
                                            </Form.Group>
                                            {type === 'slide' && (
                                                <Form.Group className='pt-3' controlId={`only_title_${language}`}>
                                                    <Form.Switch
                                                        label={ADMIN_LANG.only_title}
                                                        className='form-label'
                                                        checked={only_title}
                                                        onChange={() => dispatch(changeQuestionModal({only_title: !only_title}))}
                                                    />
                                                </Form.Group>
                                            )}
                                        </Col>
                                        {showDescription &&
                                            <Col xs={12} lg={6}>
                                                <Form.Group controlId={`description_${language}`}>
                                                    <Form.Label>{`${ADMIN_LANG.description} (${language.toUpperCase()})`}</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={2}
                                                        value={languages.find((lang) => lang.language_code === language)?.description || ''}
                                                        onChange={handleDescriptionChange}
                                                        disabled={!languageExists}
                                                    />
                                                    <Form.Text className='help-text'>
                                                        {ADMIN_LANG.description_info}
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                        }

                                        {showOutro &&
                                            <Col xs={12} lg={6}>
                                                <Form.Group controlId={`outro_text_${language}`}>
                                                    <Form.Label>{`${ADMIN_LANG.outro_text} (${language.toUpperCase()})`}</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={2}
                                                        value={languages.find((lang) => lang.language_code === language)?.outro_text || ''}
                                                        onChange={handleOutroTextChange}
                                                        disabled={!languageExists}
                                                    />
                                                    <Form.Text className='help-text'>
                                                        {ADMIN_LANG.outro_text_info}
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                        }
                                    </Row>
                                    <div className='row pt-4 gy-3 pb-3'>
                                        {content}
                                    </div>
                                    {type && !type.startsWith('element_') && type !== 'slide' && (
                                        <hr className='pb-3'></hr>
                                    )}
                                    <Row>
                                        {type === 'select' && (
                                            <Col xs={12} lg={6}>
                                                <Form.Group controlId='layout' className='pb-3'>
                                                    <Form.Label>{ADMIN_LANG.layout}</Form.Label>
                                                    <Form.Select defaultValue={layout}
                                                                 onChange={(e) => handleLayOutChange(e.target.value)}>
                                                        <option value='default'>{ADMIN_LANG.default}</option>
                                                        <option value='tag'>{ADMIN_LANG.tag_layout}</option>
                                                        <option value='twin_columns'>{ADMIN_LANG.twin_columns}</option>
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                        )}
                                        {type && !type.startsWith('element_') && type !== 'slide' && (
                                            <>
                                                <Col xs={12} lg={6}>
                                                    <Form.Group controlId='time_limit' className='pb-3'>
                                                        <Form.Label>{ADMIN_LANG.time_limit}</Form.Label>
                                                        <Form.Control onChange={handleTimeLimitChange} type='number'
                                                                      value={time_limit}/>
                                                        <Form.Text className='help-text'>
                                                            {ADMIN_LANG.time_limit_help}
                                                        </Form.Text>
                                                    </Form.Group>
                                                </Col>
                                                <Form.Group controlId='allow_skip' className='pb-3'>
                                                    <Form.Switch
                                                        checked={allow_skip}
                                                        onChange={(e) => dispatch(changeQuestionModal({allow_skip: e.target.checked}))}
                                                        className='form-label'
                                                        label={ADMIN_LANG.allow_skip}
                                                    />
                                                </Form.Group>
                                            </>

                                        )}

                                    </Row>
                                </Tab>
                            ))}
                        </Tabs>
                    </Col>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                {content_id && is_draft && (
                    <Button variant='danger' className='me-auto' onClick={handleDelete}>
                        {ADMIN_LANG.delete}
                    </Button>
                )}
                <Button variant='outline-primary' onClick={handleClose}>
                    {ADMIN_LANG.close}
                </Button>
                <Button variant='primary' type='submit' form='question-modal-form'>
                    {continueText}
                </Button>
            </Modal.Footer>
            <Confirm
                show={showConfirmDelete}
                message={ADMIN_LANG.confirm_delete_question}
                onConfirm={handleConfirm}
                onCancel={() => setShowConfirmDelete(false)}
            />
            <ElementModal/>
        </Modal>
    );
};

export default QuestionEditModal;